import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';

import { fetchGogglesUsage } from '../services/reportsService';
import { useError } from '../contexts/ErrorContext';
import { sortVersionsDesc } from '../utils/versionSort';
import { formatHours } from '../utils/formatHours';
import { ANALYTICS_TIMEZONE, wallTimeToInstant, formatInTimeZone } from '../utils/timezone';
import Modal from './Modal';
import AnalyticsTimezoneNote from './AnalyticsTimezoneNote';
import './GogglesUsageReport.css';

const HOURS_BAR_COLOR = '#38bdf8';  // sky — connected hours
const USERS_LINE_COLOR = '#fbbf24'; // amber — matches the Active Skiers trace in Goggles Connectivity

/** How many of the longest sessions the side table ranks. */
const TOP_SESSIONS_LIMIT = 100;

const today = new Date().toISOString().slice(0, 10);
const defaultStart = (() => {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return d.toISOString().slice(0, 10);
})();

const TIME_FORMAT = { hour: 'numeric', minute: '2-digit' };

/** "9:16 AM – 2:04 PM" for one session's wall-clock span, in the analytics zone. */
const formatSessionSpan = (session) => {
  if (!session?.start_time) return null;
  const start = formatInTimeZone(session.start_time, ANALYTICS_TIMEZONE, TIME_FORMAT);
  if (!session.end_time) return start;
  const end = formatInTimeZone(session.end_time, ANALYTICS_TIMEZONE, TIME_FORMAT);
  return `${start} – ${end}`;
};

const MultiSelect = ({ label, options, selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const allSelected = selected.length === options.length;
  const noneSelected = selected.length === 0;

  const toggle = (val) => {
    onChange(selected.includes(val) ? selected.filter(v => v !== val) : [...selected, val]);
  };

  const buttonLabel = noneSelected
    ? `${label}: None`
    : allSelected
      ? `${label}: All`
      : `${label}: ${selected.length}/${options.length}`;

  return (
    <div className="gu-multiselect" ref={ref}>
      <button className="gu-multiselect-btn" onClick={() => setOpen(!open)}>
        {buttonLabel}
        <span className={`gu-multiselect-chevron ${open ? 'open' : ''}`}>&#9662;</span>
      </button>
      {open && (
        <div className="gu-multiselect-dropdown">
          <div className="gu-multiselect-actions">
            <button onClick={() => onChange([...options])}>All</button>
            <button onClick={() => onChange([])}>None</button>
          </div>
          <div className="gu-multiselect-options">
            {options.map(opt => (
              <label key={opt} className="gu-multiselect-option">
                <input type="checkbox" checked={selected.includes(opt)} onChange={() => toggle(opt)} />
                <span>{opt}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  return (
    <div className="gu-chart-tooltip">
      <div className="gu-chart-tooltip-date">{label}</div>
      <div className="gu-chart-tooltip-row">
        <span className="gu-chart-tooltip-swatch" style={{ background: HOURS_BAR_COLOR }} />
        <span className="gu-chart-tooltip-label">Connected hours</span>
        <span className="gu-chart-tooltip-value">{formatHours(row.hours)}</span>
      </div>
      <div className="gu-chart-tooltip-row">
        <span className="gu-chart-tooltip-swatch" style={{ background: USERS_LINE_COLOR }} />
        <span className="gu-chart-tooltip-label">Skiers</span>
        <span className="gu-chart-tooltip-value">{(row.users || 0).toLocaleString()}</span>
      </div>
      <div className="gu-chart-tooltip-divider" />
      <div className="gu-chart-tooltip-row">
        <span className="gu-chart-tooltip-swatch" />
        <span className="gu-chart-tooltip-label">Sessions</span>
        <span className="gu-chart-tooltip-value">{(row.sessions || 0).toLocaleString()}</span>
      </div>
      <div className="gu-chart-tooltip-row">
        <span className="gu-chart-tooltip-swatch" />
        <span className="gu-chart-tooltip-label">Avg per skier</span>
        <span className="gu-chart-tooltip-value">
          {row.users > 0 ? formatHours(row.hours / row.users) : '—'}
        </span>
      </div>
    </div>
  );
};

/**
 * How a connected session is defined, rendered in the "?" modal beside the
 * report title. Thresholds come from the API response rather than being
 * restated here, so the panel can't drift from backend/reports/goggles_usage/
 * sessions.js.
 */
const MethodologyBody = ({ methodology }) => {
  const minSec = methodology?.min_session_seconds ?? 90;
  const maxHours = methodology?.max_session_hours ?? 20;
  const fwEvent = methodology?.firmware_event ?? 'GogglesVersionReceived';
  const fwParam = methodology?.firmware_param ?? 'swVersion';

  return (
    <>
      <section className="gu-info-section">
        <h4>What is measured</h4>
        <div className="gu-info-section-body">
          One <strong>connected session</strong> is a <code>GogglesConnected</code> event and
          the <code>GogglesDisconnected</code> event that closes it. Its duration is the time
          between the two. A day&apos;s bar is the sum of every skier&apos;s session durations
          that day, so it reads as total goggles-connected hours across the whole cohort.
        </div>
      </section>

      <section className="gu-info-section">
        <h4>A session must be closed</h4>
        <div className="gu-info-section-body">
          A connection that is never followed by a <code>GogglesDisconnected</code> is
          discarded, not estimated. This happens when the app is force-quit or crashes: the next event for
          that skier is another <code>GogglesConnected</code>, and the real end of the earlier
          connection is unknowable. About 14% of connections end this way.
        </div>
      </section>

      <section className="gu-info-section">
        <h4>Sessions shorter than {minSec} seconds are ignored</h4>
        <div className="gu-info-section-body">
          The reconnect-loop bug produces long runs of ~20-second connections — one skier
          logged 996 of them in a single day. Counting them buries the day&apos;s real
          sessions. Over February 2026 this floor removed 82% of raw intervals (61,816 →
          11,328) and 2.5% of total hours, and took the worst day from 996 sessions to 30.
          Connection failures themselves are the Goggles Connectivity report&apos;s subject.
        </div>
      </section>

      <section className="gu-info-section">
        <h4>Sessions longer than {maxHours} hours are ignored</h4>
        <div className="gu-info-section-body">
          A phone that goes quiet can emit its <code>GogglesDisconnected</code> days after the
          connection opened. Five such intervals in February 2026 carried 939 hours — 7.8% of
          the month from 0.008% of its sessions.
        </div>
      </section>

      <section className="gu-info-section">
        <h4>Which day a session counts toward</h4>
        <div className="gu-info-section-body">
          The day the session <strong>started</strong>, so a session belongs to exactly one
          day and its hours are never split. Days are cut in the GA4 property&apos;s own
          timezone, not the viewer&apos;s — a session running 11:30pm to 12:45am counts
          entirely on the earlier day.
        </div>
      </section>

      <section className="gu-info-section">
        <h4>A firmware version is required</h4>
        <div className="gu-info-section-body">
          Every counted session carries a firmware version reported by the goggles themselves:
          the <code>{fwParam}</code> param of the first <code>{fwEvent}</code> event falling
          inside the session, sent over BLE just after connecting. A session with no such
          event is <strong>excluded entirely</strong> — there is no <code>unknown</code>
          bucket.
          {' '}Two things cause an exclusion. The event only began shipping on{' '}
          <strong>{methodology?.firmware_event_first_day ?? '2026-03-14'}</strong>, so there is no
          data before that date. After it, exclusion is all-or-nothing by app version: some
          builds never send the event, on either platform, and every session on those builds is
          excluded. The notice above the chart counts both, and names the app versions
          responsible in the current range.
        </div>
      </section>

      <section className="gu-info-section">
        <h4>Top {TOP_SESSIONS_LIMIT} longest sessions</h4>
        <div className="gu-info-section-body">
          The table on the right ranks <strong>individual</strong> sessions by duration,
          longest first, over whatever is in scope — the selected day, or the whole date
          range when no day is selected. Each row is one unbroken connection, so it answers
          &quot;what is the longest anyone stayed connected&quot;, which the per-skier totals
          on the left cannot: a skier showing 6 hours may have skied one long run or a dozen
          short ones.
          {' '}One skier can hold several of the rows. Every row links to that exact
          session, and the same {minSec}-second floor and {maxHours}-hour ceiling apply, so
          nothing here is churn or a connection the app forgot to close.
        </div>
      </section>

      <section className="gu-info-section">
        <h4>App and device</h4>
        <div className="gu-info-section-body">
          Taken from the opening <code>GogglesConnected</code> event, so a session is
          attributed to the app build that started it.
        </div>
      </section>
    </>
  );
};

/** How many app versions to name in the exclusion notice before "+N more". */
const EXCLUDED_VERSIONS_SHOWN = 6;

/**
 * Amber notice for sessions dropped because the goggles never reported a
 * firmware version inside them. Names the actual cause for each half of
 * `excluded` (see backend/reports/goggles_usage/index.js): the date the event
 * first shipped, and the specific app versions that don't send it.
 */
const ExcludedNotice = ({ excluded, methodology, isEmpty, onOpenMethodology }) => {
  const fwEvent = methodology?.firmware_event || 'GogglesVersionReceived';
  const firstDay = methodology?.firmware_event_first_day;
  const before = excluded.before_event?.session_count || 0;
  const versions = excluded.after_event?.by_app_version || [];
  const after = excluded.after_event?.session_count || 0;

  const byPlatform = (platform) => versions.filter((v) => v.platform === platform);
  const shown = versions.slice(0, EXCLUDED_VERSIONS_SHOWN);
  const hidden = versions.length - shown.length;
  const renderPlatform = (platform, label) => {
    const list = shown.filter((v) => v.platform === platform);
    if (list.length === 0) return null;
    return (
      <span className="gu-excluded-note-platform">
        {label}{' '}
        {list.map((v, i) => (
          <React.Fragment key={`${v.platform}-${v.app_version}`}>
            {i > 0 && ', '}
            <code>{v.app_version || 'unknown'}</code> ({v.session_count.toLocaleString()})
          </React.Fragment>
        ))}
      </span>
    );
  };

  return (
    <div className={`gu-excluded-note ${isEmpty ? 'gu-excluded-note-empty' : ''}`}>
      <span className="gu-excluded-note-icon">!</span>
      <div className="gu-excluded-note-body">
        <div>
          {isEmpty ? (
            <strong>No sessions in this range report a goggles firmware version.</strong>
          ) : (
            <>
              <strong>
                {excluded.session_count.toLocaleString()} session{excluded.session_count === 1 ? '' : 's'}
                {' '}({formatHours(excluded.total_hours)})
              </strong>{' '}
              excluded from every number below: no <code>{fwEvent}</code> inside the session.
            </>
          )}
          <button type="button" className="gu-excluded-note-link" onClick={onOpenMethodology}>
            How hours are calculated
          </button>
        </div>
        {before > 0 && (
          <div className="gu-excluded-note-reason">
            <strong>{before.toLocaleString()}</strong> before {firstDay || 'the event shipped'}, when{' '}
            <code>{fwEvent}</code> first shipped.
          </div>
        )}
        {after > 0 && (
          <div className="gu-excluded-note-reason">
            <strong>{after.toLocaleString()}</strong> from app versions that don&apos;t send it:{' '}
            {renderPlatform('IOS', 'iOS')}
            {byPlatform('IOS').some((v) => shown.includes(v)) && byPlatform('ANDROID').some((v) => shown.includes(v)) && ' · '}
            {renderPlatform('ANDROID', 'Android')}
            {hidden > 0 && <span className="gu-excluded-note-more"> +{hidden} more</span>}
          </div>
        )}
      </div>
    </div>
  );
};

const GogglesUsageReport = () => {
  const { addError } = useError();
  const [searchParams, setSearchParams] = useSearchParams();
  const [startDate, setStartDate] = useState(() => searchParams.get('start') || defaultStart);
  const [endDate, setEndDate] = useState(() => searchParams.get('end') || today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [infoOpen, setInfoOpen] = useState(false);

  // Available filter options (populated from the first unfiltered fetch)
  const [allIosVersions, setAllIosVersions] = useState([]);
  const [allAndroidVersions, setAllAndroidVersions] = useState([]);
  const [allFirmwareVersions, setAllFirmwareVersions] = useState([]);
  const [selectedIosVersions, setSelectedIosVersions] = useState([]);
  const [selectedAndroidVersions, setSelectedAndroidVersions] = useState([]);
  const [selectedFirmware, setSelectedFirmware] = useState([]);

  // A version subset pinned by a shared URL (?iosVersions=1.14.46,1.14.50).
  // Consumed once, the first time the full option lists are known, then cleared
  // so later fetches don't keep re-narrowing a selection the user has since
  // changed by hand.
  const urlIosVersionsRef = useRef(searchParams.get('iosVersions'));
  const urlAndroidVersionsRef = useRef(searchParams.get('androidVersions'));
  const urlFirmwareRef = useRef(searchParams.get('firmware'));

  const [selectedDay, setSelectedDay] = useState(() => searchParams.get('day') || null);

  // Which multi-session rows are expanded to show their individual sessions.
  // Keyed by user_identifier; stale on a new fetch.
  const [expandedUsers, setExpandedUsers] = useState({});

  // A filter selection is only valid for the date range it was populated from —
  // reusing it after the range changes can silently zero the whole report if
  // the old versions don't appear in the new range.
  // Whether the filter option lists have been populated for the current date
  // range. A ref, not derived from allIosVersions/allAndroidVersions state:
  // handleFetch re-invokes itself to apply a shared link's version subset, and
  // that nested call runs inside the *same* closure, where the state still
  // reads as empty. Checking state there made the nested call re-populate the
  // dropdowns and reset them to "All" while the data it fetched was filtered —
  // and the URL-sync effect then stripped the subset from the address bar.
  const optionsKnownRef = useRef(false);

  const resetFilters = useCallback(() => {
    optionsKnownRef.current = false;
    setAllIosVersions([]);
    setAllAndroidVersions([]);
    setAllFirmwareVersions([]);
    setSelectedIosVersions([]);
    setSelectedAndroidVersions([]);
    setSelectedFirmware([]);
  }, []);

  const clearDay = useCallback(() => setSelectedDay(null), []);

  const handleFetch = useCallback(async (overrides = {}) => {
    setLoading(true);
    setError(null);
    setExpandedUsers({});
    try {
      // iosVersions/androidVersions are only sent once the option lists are
      // known — an empty array is a real filter value ("exclude this
      // platform"), not "no restriction", so they can't be sent before the
      // first unfiltered fetch has told us what's actually in this range.
      const versionsKnown = 'iosVersions' in overrides || optionsKnownRef.current;
      const filters = {
        ...(versionsKnown && {
          iosVersions: overrides.iosVersions ?? selectedIosVersions,
          androidVersions: overrides.androidVersions ?? selectedAndroidVersions,
        }),
        gogglesFirmwareVersions: overrides.gogglesFirmwareVersions ?? selectedFirmware,
      };
      const result = await fetchGogglesUsage(startDate, endDate, addError, filters);

      // Populate the filter dropdowns from the first unfiltered fetch. Both
      // lists come straight from the backend, which derives them from the
      // sessions actually in this range.
      if (!optionsKnownRef.current) {
        const iosVers = sortVersionsDesc(result.availableVersions?.ios || []);
        const androidVers = sortVersionsDesc(result.availableVersions?.android || []);
        const fwVers = sortVersionsDesc(result.availableFirmware || []);
        if (iosVers.length || androidVers.length) {
          optionsKnownRef.current = true;
          setAllIosVersions(iosVers);
          setAllAndroidVersions(androidVers);
          setAllFirmwareVersions(fwVers);

          // Apply a shared URL's version subset, once, against the now-known
          // full lists — anything named in the URL that doesn't actually appear
          // in this date range is dropped rather than zeroing everything.
          const pick = (ref, available) => (
            ref.current ? ref.current.split(',').filter((v) => available.includes(v)) : null
          );
          const urlIosSubset = pick(urlIosVersionsRef, iosVers);
          const urlAndroidSubset = pick(urlAndroidVersionsRef, androidVers);
          const urlFwSubset = pick(urlFirmwareRef, fwVers);
          urlIosVersionsRef.current = null;
          urlAndroidVersionsRef.current = null;
          urlFirmwareRef.current = null;

          const initialIos = urlIosSubset?.length ? urlIosSubset : iosVers;
          const initialAndroid = urlAndroidSubset?.length ? urlAndroidSubset : androidVers;
          const initialFw = urlFwSubset?.length ? urlFwSubset : fwVers;
          setSelectedIosVersions(initialIos);
          setSelectedAndroidVersions(initialAndroid);
          setSelectedFirmware(initialFw);

          const fwIsFiltered = initialFw.length < fwVers.length;
          if (initialIos.length < iosVers.length || initialAndroid.length < androidVers.length || fwIsFiltered) {
            // Re-fetch with the URL's subset applied — the same second call
            // "Apply Filters" makes after a manual selection. Awaited, and the
            // unfiltered `result` is never stored, so the spinner stays up
            // until the filtered data lands instead of flashing the unfiltered
            // numbers first.
            await handleFetch({
              iosVersions: initialIos,
              androidVersions: initialAndroid,
              gogglesFirmwareVersions: fwIsFiltered ? initialFw : [],
            });
            return;
          }
        }
      }

      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [
    startDate, endDate, addError, selectedIosVersions, selectedAndroidVersions, selectedFirmware,
  ]);

  const handleFilterChange = useCallback((type, values) => {
    if (type === 'ios') setSelectedIosVersions(values);
    else if (type === 'android') setSelectedAndroidVersions(values);
    else setSelectedFirmware(values);
  }, []);

  const handleApplyFilters = useCallback(() => {
    if (!data) return;

    // Deselecting every option on either axis can't match a session, so show an
    // empty report rather than sending a query whose answer is known.
    if ((selectedIosVersions.length === 0 && selectedAndroidVersions.length === 0) || selectedFirmware.length === 0) {
      setData(prev => ({
        ...prev,
        daily: [],
        users: [],
        usersByDay: {},
        summary: { total_hours: 0, total_sessions: 0, total_unique_users: 0, total_user_days: 0, avg_hours_per_user_day: 0 },
      }));
      setSelectedDay(null);
      return;
    }

    handleFetch({
      iosVersions: selectedIosVersions,
      androidVersions: selectedAndroidVersions,
      gogglesFirmwareVersions: selectedFirmware.length === allFirmwareVersions.length ? [] : selectedFirmware,
    });
  }, [data, handleFetch, selectedIosVersions, selectedAndroidVersions, selectedFirmware, allFirmwareVersions.length]);

  // Keep the URL in sync with the current view — dates, selected day, and
  // version filters (only when they're a real subset; "all" is represented by
  // omitting the param) — so this exact view is a shareable link. Merges with
  // whatever else is already in the query string (e.g. AnalyticsPage's `report`
  // param) instead of replacing it.
  useEffect(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('start', startDate);
      next.set('end', endDate);

      if (selectedDay) next.set('day', selectedDay);
      else next.delete('day');

      const setSubset = (key, selected, all) => {
        if (all.length > 0 && selected.length < all.length) next.set(key, selected.join(','));
        else next.delete(key);
      };
      setSubset('iosVersions', selectedIosVersions, allIosVersions);
      setSubset('androidVersions', selectedAndroidVersions, allAndroidVersions);
      setSubset('firmware', selectedFirmware, allFirmwareVersions);

      return next;
    }, { replace: true });
  }, [
    startDate, endDate, selectedDay, selectedIosVersions, selectedAndroidVersions, selectedFirmware,
    allIosVersions, allAndroidVersions, allFirmwareVersions, setSearchParams,
  ]);

  // A URL carrying report state (a shared link) runs the report on load instead
  // of leaving the user to click "Run Report" themselves.
  const autoFetchedRef = useRef(false);
  useEffect(() => {
    if (autoFetchedRef.current) return;
    autoFetchedRef.current = true;
    const hasUrlState = ['start', 'end', 'day', 'iosVersions', 'androidVersions', 'firmware']
      .some((k) => searchParams.has(k));
    if (hasUrlState) handleFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // usersByDay already came back with the range fetch, so selecting a day is a
  // pure client-side slice — no refetch, no loading gap, and the table can't
  // disagree with the chart since both read the same computation.
  const handleDayClick = useCallback((day) => {
    if (!day) return;
    setSelectedDay((prev) => (prev === day ? null : day));
    setExpandedUsers({});
  }, []);

  // Deep-link into a user's analytics for one specific session. The day
  // boundaries are resolved in ANALYTICS_TIMEZONE and sent as absolute
  // instants, with `tz` so the target page displays the same window —
  // otherwise a bare "2026-04-03T00:00" would be read in the viewer's own
  // timezone and land on a different day. `link_start_day`/`link_end_day` come
  // from the backend and already span both the session itself and the
  // neighbouring event its ga_session_id was borrowed from, which the receiving
  // page needs in view to resolve the same id.
  const getSessionHref = useCallback((uid, session) => {
    if (!uid || !session?.link_start_day) return null;
    const start = wallTimeToInstant(`${session.link_start_day}T00:00`, ANALYTICS_TIMEZONE);
    const end = wallTimeToInstant(`${session.link_end_day || session.link_start_day}T23:59`, ANALYTICS_TIMEZONE);
    if (!start || !end) return `/users/${uid}`;
    const params = new URLSearchParams({
      tab: 'analytics',
      startDate: start.toISOString(),
      endDate: end.toISOString(),
      tz: ANALYTICS_TIMEZONE,
    });
    if (session.session_id) params.set('session', session.session_id);
    return `/users/${uid}?${params.toString()}`;
  }, []);

  // Whole-day fallback for a row whose sessions carry no resolvable
  // ga_session_id. Links into the day(s) the row covers rather than dropping to
  // a bare profile page.
  const getDayHref = useCallback((uid, day, endDay = null) => {
    if (!uid || !day) return null;
    const start = wallTimeToInstant(`${day}T00:00`, ANALYTICS_TIMEZONE);
    const end = wallTimeToInstant(`${endDay && endDay > day ? endDay : day}T23:59`, ANALYTICS_TIMEZONE);
    if (!start || !end) return `/users/${uid}`;
    const params = new URLSearchParams({
      tab: 'analytics',
      startDate: start.toISOString(),
      endDate: end.toISOString(),
      tz: ANALYTICS_TIMEZONE,
    });
    return `/users/${uid}?${params.toString()}`;
  }, []);

  // A row with no uid has no user page to open
  const handleUserClick = useCallback((e, user) => {
    if (!user.user_id) e.preventDefault();
  }, []);

  // One row per day. The bar is total connected hours; the line is how many
  // distinct skiers those hours came from, on its own axis since hours and
  // skiers aren't the same unit.
  const chartData = useMemo(() => (data?.daily || []).map((d) => ({
    date: d.date,
    hours: d.total_hours || 0,
    users: d.unique_users || 0,
    sessions: d.session_count || 0,
  })), [data]);

  // The table reads the selected day's slice, or the whole-range roll-up when
  // no day is selected. Both shipped with the same fetch.
  const tableRows = useMemo(() => {
    if (!data) return [];
    if (selectedDay) return data.usersByDay?.[selectedDay] || [];
    return data.users || [];
  }, [data, selectedDay]);

  const tableTotals = useMemo(() => tableRows.reduce((acc, r) => ({
    hours: acc.hours + (r.hours || 0),
    sessions: acc.sessions + (r.session_count || 0),
  }), { hours: 0, sessions: 0 }), [tableRows]);

  // The longest individual connected sessions in scope, ranked by duration.
  // Every session is one unbroken stretch of connection by construction (a
  // GogglesConnected and the GogglesDisconnected that closes it), so this is
  // the longest contiguous blocks of connected time, not a sum.
  //
  // Derived from the same rows the skier table reads rather than fetched
  // separately, so the two tables cannot disagree and a day switch stays a
  // pure client-side slice. Inherits MAX_USERS_PER_DAY from the backend: a
  // session belonging to a skier past that per-day cap isn't in the payload to
  // rank. The cap is 500 against a measured peak of 299 skiers/day.
  const topSessions = useMemo(() => {
    const out = [];
    for (const u of tableRows) {
      for (const s of (u.by_session || [])) {
        out.push({
          ...s,
          user_identifier: u.user_identifier,
          user_id: u.user_id,
          user_name: u.user_name,
        });
      }
    }
    return out
      .sort((a, b) => (b.duration_sec || 0) - (a.duration_sec || 0))
      .slice(0, TOP_SESSIONS_LIMIT);
  }, [tableRows]);

  const summary = data?.summary;
  const dayCount = data?.daily?.length || 0;

  return (
    <div className="gu-report">
      <div className="gu-header">
        <div className="gu-header-left">
          <div className="gu-title-section">
            <h2 className="gu-title">
              Goggles Usage
              <button
                type="button"
                className="gu-title-info-btn"
                title="How connected hours are calculated"
                aria-label="How connected hours are calculated"
                onClick={() => setInfoOpen(true)}
              >
                ?
              </button>
            </h2>
            <p className="gu-subtitle">Hours skied with the goggles connected</p>
          </div>
        </div>
        <div className="gu-controls">
          <input
            type="date"
            className="gu-date-input"
            value={startDate}
            onChange={e => { setStartDate(e.target.value); resetFilters(); setSelectedDay(null); }}
          />
          <span className="gu-date-sep">to</span>
          <input
            type="date"
            className="gu-date-input"
            value={endDate}
            onChange={e => { setEndDate(e.target.value); resetFilters(); setSelectedDay(null); }}
          />
          <button className="gu-fetch-btn" onClick={() => handleFetch()} disabled={loading}>
            {loading ? 'Loading...' : 'Run Report'}
          </button>
        </div>
      </div>

      <AnalyticsTimezoneNote timezone={data?.timezone} />

      {/* Filter bar — shown after first fetch */}
      {(allIosVersions.length > 0 || allAndroidVersions.length > 0) && (
        <div className="gu-filters">
          <MultiSelect
            label="iOS Version"
            options={allIosVersions}
            selected={selectedIosVersions}
            onChange={(vals) => handleFilterChange('ios', vals)}
          />
          <MultiSelect
            label="Android Version"
            options={allAndroidVersions}
            selected={selectedAndroidVersions}
            onChange={(vals) => handleFilterChange('android', vals)}
          />
          <MultiSelect
            label="Firmware"
            options={allFirmwareVersions}
            selected={selectedFirmware}
            onChange={(vals) => handleFilterChange('fw', vals)}
          />
          <button className="gu-filter-apply" onClick={handleApplyFilters} disabled={loading}>
            Apply Filters
          </button>
        </div>
      )}

      {error && <div className="gu-error">{error}</div>}

      {!data && !loading && !error && (
        <div className="gu-empty">Select a date range and click Run Report</div>
      )}

      {loading && (
        <div className="gu-loading">
          <div className="gu-spinner" />
          <p>Querying Analytics...</p>
        </div>
      )}

      {/* Sessions dropped by the hard firmware requirement, with the reason
          split the way the backend splits it: before the event shipped, versus
          app versions that still don't send it. Without this a pre-launch range
          renders as an empty chart that reads as "nobody skied". */}
      {data && !loading && data.excluded?.session_count > 0 && (
        <ExcludedNotice
          excluded={data.excluded}
          methodology={data.methodology}
          isEmpty={(data.daily?.length || 0) === 0}
          onOpenMethodology={() => setInfoOpen(true)}
        />
      )}

      {data && !loading && (
        <div className="gu-content">
          {/* Range summary */}
          <div className="gu-summary-cards">
            <div className="gu-summary-card">
              <div className="gu-summary-card-label">Connected Hours</div>
              <div className="gu-summary-card-value">{formatHours(summary?.total_hours)}</div>
              <div className="gu-summary-card-sub">
                {dayCount > 0 ? `${formatHours((summary?.total_hours || 0) / dayCount)}/day avg` : '—'}
              </div>
            </div>
            <div className="gu-summary-card">
              <div className="gu-summary-card-label">Skiers</div>
              <div className="gu-summary-card-value">{(summary?.total_unique_users || 0).toLocaleString()}</div>
              <div className="gu-summary-card-sub">{(summary?.total_user_days || 0).toLocaleString()} skier-days</div>
            </div>
            <div className="gu-summary-card">
              <div className="gu-summary-card-label">Sessions</div>
              <div className="gu-summary-card-value">{(summary?.total_sessions || 0).toLocaleString()}</div>
              <div className="gu-summary-card-sub">
                {summary?.total_sessions > 0
                  ? `${formatHours((summary.total_hours || 0) / summary.total_sessions)} each avg`
                  : '—'}
              </div>
            </div>
            <div className="gu-summary-card">
              <div className="gu-summary-card-label">Per Skier-Day</div>
              <div className="gu-summary-card-value">{formatHours(summary?.avg_hours_per_user_day)}</div>
              <div className="gu-summary-card-sub">average day on snow</div>
            </div>
          </div>

          {/* Daily connected hours */}
          <div className="gu-chart-section">
            <div className="gu-chart-header">
              <h3 className="gu-section-title">
                Daily Connected Hours
                <span className="gu-section-sub">
                  every skier&apos;s connected sessions summed per day · Skiers (line — distinct
                  skiers contributing those hours) · click a bar to scope the table below
                </span>
              </h3>
            </div>
            <div className="gu-chart-container">
              <ResponsiveContainer width="100%" height={320}>
                <ComposedChart data={chartData} margin={{ top: 16, right: 24, left: 8, bottom: 8 }}>
                  <CartesianGrid stroke="#1f1f1f" vertical={false} />
                  <XAxis
                    dataKey="date"
                    stroke="#444"
                    tick={({ x, y, payload }) => (
                      <g transform={`translate(${x},${y})`} style={{ cursor: 'pointer' }} onClick={() => handleDayClick(payload.value)}>
                        <text
                          dy={12}
                          textAnchor="middle"
                          fill={selectedDay === payload.value ? '#fff' : '#999'}
                          fontSize={11}
                          fontWeight={selectedDay === payload.value ? 600 : 400}
                          fontFamily="SF Mono, Monaco, monospace"
                        >
                          {payload.value ? payload.value.slice(5) : ''}
                        </text>
                      </g>
                    )}
                  />
                  {/* Two axes here, unlike the single shared axis in Goggles
                      Connectivity: hours and skiers are different units, so
                      there is no honest ratio to preserve between them. */}
                  <YAxis
                    yAxisId="hours"
                    stroke="#444"
                    tick={{ fill: '#999', fontSize: 11, fontFamily: 'SF Mono, Monaco, monospace' }}
                    label={{ value: 'hours', position: 'insideTopLeft', fill: '#666', fontSize: 10, dy: -10 }}
                  />
                  <YAxis
                    yAxisId="users"
                    orientation="right"
                    stroke="#444"
                    allowDecimals={false}
                    tick={{ fill: '#999', fontSize: 11, fontFamily: 'SF Mono, Monaco, monospace' }}
                    label={{ value: 'skiers', position: 'insideTopRight', fill: '#666', fontSize: 10, dy: -10 }}
                  />
                  <Tooltip cursor={{ fill: 'rgba(255,255,255,0.04)' }} content={<ChartTooltip />} />
                  <Bar
                    yAxisId="hours"
                    dataKey="hours"
                    name="Connected hours"
                    fill={HOURS_BAR_COLOR}
                    // A day with a handful of minutes against a busy month can
                    // round to nothing — floor a genuinely nonzero value to 2px
                    // so it stays visible and clickable. Floor only nonzero
                    // values, or empty days draw a 2px sliver too.
                    minPointSize={(value) => (value > 0 ? 2 : 0)}
                    isAnimationActive={false}
                    cursor="pointer"
                    onClick={(barData) => {
                      const day = barData?.payload?.date || barData?.date;
                      if (day) handleDayClick(day);
                    }}
                  />
                  <Line
                    yAxisId="users"
                    dataKey="users"
                    stroke={USERS_LINE_COLOR}
                    strokeWidth={2.5}
                    dot={{ strokeWidth: 0, r: 3, fill: USERS_LINE_COLOR }}
                    activeDot={{ strokeWidth: 0, r: 4.5, fill: USERS_LINE_COLOR }}
                    isAnimationActive={false}
                    name="Skiers"
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Both tables share one scope: the selected day, or the whole range.
              Left totals each skier's day; right ranks the individual sessions
              those totals are made of. */}
          <div className="gu-tables-row">
          <div className="gu-chart-section gu-tables-row-main">
            <h3 className="gu-section-title">
              Hours by Skier
              <span className="gu-scope-chips">
                <span className="gu-scope-chip">
                  {selectedDay || `${startDate} → ${endDate}`}
                  {selectedDay && (
                    <button className="gu-scope-chip-clear" onClick={clearDay} title="Clear day filter">&times;</button>
                  )}
                </span>
              </span>
            </h3>
            <div className="gu-table gu-table-wide">
              <div className="gu-table-header gu-table-header-users">
                <span>Skier</span>
                <span>Hours</span>
                <span>Sessions</span>
                <span>Firmware</span>
                <span>App</span>
                <span>Device</span>
                <span></span>
              </div>
              {tableRows.length === 0 && (
                <div className="gu-table-empty">No connected sessions in this range</div>
              )}
              {tableRows.map(user => {
                const sessions = user.by_session || [];
                const hasUid = !!user.user_id;
                // One session links straight to it; more than one makes the row
                // an expand toggle whose children each link to their own
                // session. Same pattern as Goggles Connectivity's Top Affected
                // Users table.
                const isMultiSession = hasUid && sessions.length > 1;
                const isExpanded = !!expandedUsers[user.user_identifier];
                const singleHref = !hasUid
                  ? null
                  : sessions.length === 1
                    ? getSessionHref(user.user_id, sessions[0])
                    : sessions.length === 0
                      ? getDayHref(user.user_id, selectedDay || startDate, selectedDay || endDate)
                      : null;

                const toggleExpand = () => {
                  setExpandedUsers(prev => ({ ...prev, [user.user_identifier]: !prev[user.user_identifier] }));
                };

                const RowTag = isMultiSession ? 'div' : 'a';
                const rowProps = isMultiSession
                  ? {
                      role: 'button',
                      tabIndex: 0,
                      onClick: toggleExpand,
                      onKeyDown: (e) => {
                        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleExpand(); }
                      },
                    }
                  : {
                      href: singleHref,
                      target: '_blank',
                      rel: 'noopener noreferrer',
                      onClick: (e) => handleUserClick(e, user),
                    };

                return (
                  <React.Fragment key={user.user_identifier}>
                    <RowTag
                      className={`gu-table-row gu-table-row-users ${hasUid ? 'gu-table-row-clickable' : ''} ${isMultiSession ? 'gu-table-row-expandable' : ''}`}
                      {...rowProps}
                    >
                      <span className="gu-table-cell-primary gu-user-name-cell">
                        {isMultiSession && <span className={`gu-expand-chevron ${isExpanded ? 'open' : ''}`}>&#9656;</span>}
                        {user.user_name || user.user_id || user.user_identifier.substring(0, 12) + '...'}
                      </span>
                      <span className="gu-table-cell-hours">{formatHours(user.hours)}</span>
                      <span>{(user.session_count || 0).toLocaleString()}</span>
                      <span className="gu-table-cell-muted">{(user.firmware_versions || []).join(', ') || '—'}</span>
                      <span className="gu-table-cell-muted">{(user.app_versions || []).join(', ') || '—'}</span>
                      <span className="gu-table-cell-muted">{(user.device_models || []).join(', ') || '—'}</span>
                      <span className="gu-copy-cell">
                        {user.user_id && (
                          <button
                            className="gu-copy-btn"
                            title="Copy user ID"
                            onClick={(e) => { e.stopPropagation(); e.preventDefault(); navigator.clipboard.writeText(user.user_id); }}
                          >
                            &#x1F4CB;
                          </button>
                        )}
                      </span>
                    </RowTag>
                    {isMultiSession && isExpanded && (
                      <div className="gu-table-row-sessions">
                        {sessions.map(s => (
                          <a
                            key={`${s.session_id}-${s.start_time}`}
                            href={getSessionHref(user.user_id, s)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="gu-table-row-session"
                            onClick={(e) => handleUserClick(e, user)}
                          >
                            <span className="gu-table-row-session-date">
                              {s.link_start_day}
                              <span className="gu-table-row-session-time">{formatSessionSpan(s)}</span>
                            </span>
                            <span className="gu-table-row-session-dur">{formatHours(s.duration_sec / 3600)}</span>
                            <span className="gu-table-row-session-fw">{s.goggles_sw_version}</span>
                          </a>
                        ))}
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
              {tableRows.length > 0 && (
                <div className="gu-table-row gu-table-row-users gu-table-row-total">
                  <span className="gu-table-cell-primary">
                    Total
                    <span className="gu-table-cell-muted" style={{ fontWeight: 400, fontSize: 11 }}>
                      {' '}({tableRows.length} {tableRows.length === 1 ? 'skier' : 'skiers'})
                    </span>
                  </span>
                  <span className="gu-table-cell-hours">{formatHours(tableTotals.hours)}</span>
                  <span>{tableTotals.sessions.toLocaleString()}</span>
                  <span></span>
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              )}
            </div>
          </div>

          {/* Longest individual sessions in the same scope. Each row is one
              unbroken connection, so this answers "what is the longest anyone
              stayed connected" — which the left table's per-skier totals hide,
              since a big total can be one long run or a dozen short ones. */}
          <div className="gu-chart-section gu-tables-row-side">
            <h3 className="gu-section-title">
              Top {TOP_SESSIONS_LIMIT} Longest Sessions
              <span className="gu-section-sub">
                single unbroken connections, longest first
                {/* Say so when the scope holds fewer than the cap, rather than
                    letting the "Top 100" heading imply a truncation that isn't
                    happening. */}
                {tableTotals.sessions > 0 && (
                  topSessions.length < tableTotals.sessions
                    ? ` · showing ${topSessions.length} of ${tableTotals.sessions.toLocaleString()}`
                    : ` · all ${topSessions.length}`
                )}
              </span>
            </h3>
            <div className="gu-table gu-top-sessions">
              <div className="gu-table-header gu-table-header-top-sessions">
                <span>#</span>
                <span>Duration</span>
                <span>Skier</span>
              </div>
              <div className="gu-top-sessions-scroll">
                {topSessions.length === 0 && (
                  <div className="gu-table-empty">No connected sessions in this range</div>
                )}
                {topSessions.map((s, i) => {
                  const href = s.user_id ? getSessionHref(s.user_id, s) : null;
                  const RowTag = href ? 'a' : 'div';
                  const rowProps = href
                    ? { href, target: '_blank', rel: 'noopener noreferrer', onClick: (e) => handleUserClick(e, s) }
                    : {};
                  return (
                    <RowTag
                      key={`${s.user_identifier}-${s.start_time}`}
                      className={`gu-top-session ${href ? 'gu-table-row-clickable' : ''}`}
                      {...rowProps}
                    >
                      <span className="gu-top-session-rank">{i + 1}</span>
                      <span className="gu-top-session-dur">{formatHours(s.duration_sec / 3600)}</span>
                      <span className="gu-top-session-main">
                        <span className="gu-top-session-user">
                          {s.user_name || s.user_id || s.user_identifier.substring(0, 12) + '...'}
                        </span>
                        <span className="gu-top-session-meta">
                          {/* The date only earns its place when the scope spans
                              more than one day; inside a day it is noise. */}
                          {!selectedDay && <span className="gu-top-session-day">{s.link_start_day}</span>}
                          {formatSessionSpan(s)}
                          <span className="gu-top-session-fw">{s.goggles_sw_version}</span>
                        </span>
                      </span>
                    </RowTag>
                  );
                })}
              </div>
            </div>
          </div>
          </div>
        </div>
      )}

      <Modal
        isOpen={infoOpen}
        onClose={() => setInfoOpen(false)}
        title="How connected hours are calculated"
        subtitle={<span className="gu-info-modal-subtitle">Goggles Usage · methodology</span>}
        className="gu-info-modal"
      >
        <MethodologyBody methodology={data?.methodology} />
      </Modal>
    </div>
  );
};

export default GogglesUsageReport;
